/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Error } from '../models/Error';
import type { OAuth2ProfileResponse } from '../models/OAuth2ProfileResponse';
import type { OAuth2TokenRequest } from '../models/OAuth2TokenRequest';
import type { OAuth2TokenResponse } from '../models/OAuth2TokenResponse';
import type { SetSessionUserRequest } from '../models/SetSessionUserRequest';
import type { SetSessionUserResponse } from '../models/SetSessionUserResponse';
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class Oauth2V2Service {
  /**
   * Establish user session
   *
   * Establish user session after OAuth2 authentication.
   *
   * This endpoint validates the authenticated user's access token and establishes
   * a session with the specified user_id and organization_id.
   *
   * **Critical for web app authentication flow:**
   * 1. User authenticates via OAuth2 (/oauth2/token)
   * 2. Web app receives access_token with user_id
   * 3. Web app calls /set_session_user with {user_id, org_id}
   * 4. Session is established and user can access protected resources
   *
   * **Security validations:**
   * - Validates user_id matches authenticated user from token
   * - Validates org_id belongs to the authenticated user
   * - Returns 403 if user tries to impersonate another user
   * - Returns 403 if user tries to access unauthorized organization
   *
   * **Required authentication:**
   * - Bearer token (OAuth2 access token) in Authorization header
   * - Token must have 'profile' scope
   *
   * **Response:**
   * - Returns session confirmation with user_id and org_id
   * - Session is stored server-side and linked to the access token
   *
   * @returns SetSessionUserResponse Session established successfully
   * @returns Error Default error response
   * @throws ApiError
   */
  public static postV2Oauth2SetSessionUser({
    requestBody,
  }: {
    requestBody: SetSessionUserRequest;
  }): CancelablePromise<SetSessionUserResponse | Error> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v2/oauth2/set_session_user',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized - invalid or missing access token`,
        403: `Forbidden - user/organization access denied`,
        422: `Unprocessable Entity`,
        500: `Internal server error during session establishment`,
      },
    });
  }
  /**
   * OAuth2 token issuance and refresh
   *
   * OAuth2 Token Endpoint - RFC 6749 compliant.
   *
   * **Supports multiple grant types:**
   *
   * 1. **Password Grant** (Username/Password Authentication):
   * ```
   * POST /v2/oauth2/token
   * Content-Type: application/x-www-form-urlencoded
   *
   * grant_type=password&username=user@example.com&password=secret&scope=profile
   * ```
   *
   * 2. **Refresh Token Grant** (Token Refresh):
   * ```
   * POST /v2/oauth2/token
   * Content-Type: application/x-www-form-urlencoded
   *
   * grant_type=refresh_token&refresh_token=tFnANP87rRMSCCuSwTT0Y5M0LmpUmPMbBow2F8zKCM
   * ```
   *
   * 3. **Authorization Code Grant** (OAuth2 Flow):
   * ```
   * POST /v2/oauth2/token
   * Content-Type: application/x-www-form-urlencoded
   *
   * grant_type=authorization_code&code=8xLOxBtZp8&redirect_uri=https://app.example.com/callback
   * ```
   *
   * 4. **Client Credentials Grant** (Service Authentication):
   * ```
   * POST /v2/oauth2/token
   * Content-Type: application/x-www-form-urlencoded
   * Authorization: Basic <base64(client_id:client_secret)>
   *
   * grant_type=client_credentials&scope=api
   * ```
   *
   * **Response includes:**
   * - access_token: Bearer token for API authentication
   * - token_type: Always "Bearer"
   * - expires_in: Token expiration in seconds
   * - refresh_token: Token for obtaining new access tokens (password/refresh_token grants)
   * - user_id: User ID for /set_session_user compatibility
   * - scope: Granted OAuth2 scope
   *
   * **Critical for web app:**
   * - Returns user_id in response for /set_session_user endpoint
   * - Returns refresh_token for token renewal without re-authentication
   *
   * @returns OAuth2TokenResponse Token issued successfully
   * @returns Error Default error response
   * @throws ApiError
   */
  public static postV2Oauth2Token({
    formData,
  }: {
    formData: OAuth2TokenRequest;
  }): CancelablePromise<OAuth2TokenResponse | Error> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v2/oauth2/token',
      formData: formData,
      mediaType: 'application/x-www-form-urlencoded',
      errors: {
        400: `Invalid request - missing or invalid parameters`,
        401: `Invalid credentials or token`,
        422: `Unprocessable Entity`,
        500: `Internal server error`,
      },
    });
  }
  /**
   * Get complete user profile
   *
   * Returns complete user profile with organizations and subscription details.
   *
   * This endpoint provides comprehensive user information including:
   * - User basic information (id, email, name, etc.)
   * - All organizations the user belongs to
   * - Sub-organizations within those organizations
   * - Active subscription details
   * - Total sub-organization count
   *
   * **Required authentication:**
   * - Bearer token (OAuth2 access token) in Authorization header
   * - Token must have 'profile' scope
   *
   * **Difference from /v2/auth/profile:**
   * - This endpoint returns MORE detailed organization structure
   * - Includes sub-organizations and subscription details
   * - Used by web app for complete organization management
   * - /v2/auth/profile returns simplified structure for basic needs
   *
   * @returns OAuth2ProfileResponse Profile retrieved successfully
   * @returns Error Default error response
   * @throws ApiError
   */
  public static getV2Oauth2Profile(): CancelablePromise<
    OAuth2ProfileResponse | Error
  > {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v2/oauth2/profile',
      errors: {
        401: `Unauthorized - invalid or missing access token`,
        500: `Internal server error`,
      },
    });
  }
}
